import { createContext, useContext, type ReactNode, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import useDebounce from '../../../hooks/useDebounce';

import { Form } from '../queries/schema/carFinance.schema';

type TotalDepositProviderProps = {
  minimumDepositValue: string;
  children: ReactNode;
};

type TotalDepositContext = {
  isValidDeposit: boolean;
  debouncedTotalDeposit: number;
};

const TotalDepositContext = createContext<TotalDepositContext | null>(null);

const TotalDepositProvider = (props: TotalDepositProviderProps) => {
  const { children, minimumDepositValue } = props;

  const { control } = useFormContext<Form>();

  const [totalDeposit, minimumFinanceAmount, financeAmount] = useWatch<
    Form,
    [
      'loanDetails.totalDeposit',
      'loanDetails.minimumFinanceAmount',
      'loanDetails.financeAmount',
    ]
  >({
    control,
    name: [
      'loanDetails.totalDeposit',
      'loanDetails.minimumFinanceAmount',
      'loanDetails.financeAmount',
    ],
  });

  /**
   * The total deposit is calculated using the values for the cash deposit and the trade-in vehicle,
   * less outstanding finance.
   *
   * We listen to changes in this value to determine when to request loan details to reflect these changes.
   */
  const debouncedTotalDeposit = useDebounce(totalDeposit, 500) ?? 0;

  const isValidDeposit =
    debouncedTotalDeposit >= parseInt(minimumDepositValue) &&
    financeAmount >= minimumFinanceAmount;

  const contextValue = useMemo(
    () => ({ isValidDeposit, debouncedTotalDeposit }),
    [isValidDeposit, debouncedTotalDeposit],
  );

  return (
    <TotalDepositContext.Provider value={contextValue}>
      {children}
    </TotalDepositContext.Provider>
  );
};

const useTotalDepositContext = () => {
  const context = useContext(TotalDepositContext);
  if (!context) {
    throw new Error(
      'useTotalDepositContext must be used within a TotalDepositProvider instance',
    );
  }
  return context;
};

export { TotalDepositProvider, useTotalDepositContext };
